import React, { FC, ReactElement, useCallback } from 'react'
import { friendlyTxError } from '@/utils/friendly-tx-error'
import Link from 'next/link'
import classNames from 'classnames'
import { Button } from '@/components/ui/button'
import Loading from '@/components/circle-loading'
import useDebounce from '@/utils/hooks/useDebounce'
import useFeeEstimates, { useSelectFeeRate } from '@/utils/hooks/useFeeEstimates'
import useComponentSize from '@/utils/hooks/useComponentSize'
import SelectButton from '@/components/search-input/components/select-button'
import Wallet from '@/utils/wallet'
import { Input } from '@/components/ui/input'
import { isPasswordUnlocked, getSeed } from '@/utils/password'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import formatNumber from '@/utils/format-number'
import { NETWORK } from '@/utils/constants'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import event from '@/utils/event'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

interface ItemValueParams {
	building: boolean
	fee: number
	psbt: string | null
}

interface Item {
	title: string
	value?: React.ReactNode
	valueFunc?: (p: ItemValueParams) => React.ReactNode
	emphasize?: boolean
}

let latest_key = null

const Invoice: FC<{
	open: boolean
	onSubmit: (psbt: string, setup?: string) => Promise<void>
	buildDeps?: any[]
	onBuild?: (
		deps: any[],
		feeRate: number
	) => Promise<{ psbt: string; setup?: string; fee: bigint } | null>
	action: string | ((p: ItemValueParams) => string)
	items: Item[]
	disabled?: boolean
	validateSubmit?: (p: ItemValueParams) => string | undefined
	errorMessage?: string
	debounce?: number
	inline?: boolean
	fixed?: boolean // when true, render as fixed overlay (useful for mobile to overlap bottom bars)
}> = (props): ReactElement => {
	const { exchangeRate } = useExchangeRate()
	const { speed, feeRate, selected } = useFeeEstimates()
	const { open, items, action, onSubmit, disabled, onBuild, buildDeps, errorMessage, fixed } = props
	const isSignedIn = useLoggedIn()
	const { ref, height } = useComponentSize()

	const [pass, setPass] = React.useState('')
	const [validPass, setValidPass] = React.useState(false)
	const [loading, setLoading] = React.useState(false)
	const [loadingPass, setLoadingPass] = React.useState(false)
	const [loadingPsbt, setLoadingPsbt] = React.useState(false)
	const [fee, setFee] = React.useState(0)
	const [psbt, setPsbt] = React.useState<string | null>(null)
	const [setup, setSetup] = React.useState<string | null>(null)
	const [error, setError] = React.useState<string | null>(null)
	const [retryCount, setRetryCount] = React.useState(0)
	const [showFee, setShowFee] = React.useState(false)
	const selectFee = useSelectFeeRate()

	// Dedupe upfront, so we never FIRE a duplicate onBuild HTTP burst.
	// React 18 StrictMode in dev double-invokes useEffect (cleanup + effect
	// re-run) to help catch impure-effect bugs, which meant any `onBuild`
	// that fans out to multiple HTTP calls would run its entire sequence
	// twice. The existing `latest_key` module guard discards the stale
	// RESULT, but by then we've already paid the network cost. This ref
	// records the input-serialization of the last build we kicked off, so
	// the second StrictMode invocation — which has identical deps — is
	// skipped before any POST goes out. Legitimate re-triggers (buildDeps
	// or feeRate actually changed) produce a different key and run normally.
	const startedKeyRef = React.useRef<string>('')

	const feeOptions = [
		{ level: 'Slow', rate: speed.slow, key: 'slow' },
		{ level: 'Medium', rate: speed.medium, key: 'medium' },
		{ level: 'Fast', rate: speed.fast, key: 'fast' }
	]

	const handleSelectFee = React.useCallback(
		(v: any) => {
			setShowFee(false)
			selectFee(v.key)
		},
		[selectFee]
	)

	const handleChangePass = React.useCallback((e: any) => {
		setPass(e.target.value)
		setError((prev) => (prev === 'Enter a password' ? '' : prev))
	}, [])

	const newworkFeeValueFunc = useCallback(
		({ building, fee }: ItemValueParams) => {
			return building
				? '--'
				: `${formatSatoshis(fee)} ${NETWORK} / $${formatNumber(
						((fee / 1e8) * exchangeRate)?.toFixed(2)
				  )}`
		},
		[exchangeRate]
	)

	const renderItem = React.useCallback(
		(e: any, i: any) => {
			const isPrimaryRow = e.emphasize ?? i === 0
			return (
				<div className="flex items-center justify-between select-none" key={i}>
					<p
						className={classNames({
							['text-foreground text-xs font-semibold']: isPrimaryRow,
							['text-muted-foreground text-xxs']: !isPrimaryRow
						})}
					>
						{e.title}
						{e.title === 'Network Fee' && (
							<>
								{' '}
								(
								<span
									onClick={() => setShowFee(!showFee)}
									className="underline cursor-pointer"
									role="button"
									tabIndex={0}
									onKeyDown={onKeyboardClick(() => setShowFee(!showFee))}
								>
									{selected}
								</span>
								)
							</>
						)}
					</p>
					<p
						className={classNames({
							['text-muted-foreground text-xs font-semibold']: isPrimaryRow,
							['text-muted-foreground text-xxs']: !isPrimaryRow
						})}
					>
						{e.title === 'Network Fee' &&
							newworkFeeValueFunc({ building: loadingPsbt || !!errorMessage, fee, psbt })}
						{e.valueFunc
							? e.valueFunc({ building: loadingPsbt || !!errorMessage, fee, psbt })
							: e.value}
					</p>
				</div>
			)
		},
		[errorMessage, fee, loadingPsbt, newworkFeeValueFunc, psbt, selected, showFee]
	)

	const dBuildDepsRaw = useDebounce(buildDeps, props.debounce || 500)
	const dFeeRate = useDebounce(feeRate, props.debounce || 500)
	const dBuildDeps = dBuildDepsRaw

	React.useEffect(() => {
		async function run() {
			if (!isSignedIn) {
				// When not signed in, the action button is replaced by the
				// "Connect Wallet" CTA in the render below — loadingPsbt
				// doesn't gate any visible UI in that branch. Setting it
				// to `true` here was a latent footgun: if isSignedIn ever
				// flipped back to false (SWR cache eviction, revalidation
				// race) the spinner would stick because the effect short-
				// circuits before reaching the normal `setLoadingPsbt(false)`
				// in the finally block. Keep this consistent with "no build
				// in flight".
				setLoadingPsbt(false)
				return
			}

			// @ts-ignore
			const key = new Date().toISOString()
			latest_key = key

			console.log(
				'[invoice] build triggered at=' +
					key +
					' feeRate=' +
					dFeeRate +
					' depsLength=' +
					(dBuildDeps?.length || 0) +
					' dep0Size=' +
					(dBuildDeps?.[0]?.size ?? dBuildDeps?.[0]) +
					' dep1=' +
					(typeof dBuildDeps?.[1] === 'string' ? dBuildDeps[1].slice(0, 20) : dBuildDeps?.[1])
			)

			setLoadingPsbt(true)
			setError(null)
			setSetup(null)
			setPsbt(null)
			setFee(0)

			try {
				const t0 = performance.now()
				const res = await (onBuild as any)(dBuildDeps as any, dFeeRate)
				const elapsed = (performance.now() - t0).toFixed(0)
				console.log(
					'[invoice] build completed in ' +
						elapsed +
						'ms key=' +
						key +
						' latestKey=' +
						latest_key +
						' hasResult=' +
						!!res
				)
				if (res && key === latest_key) {
					const { psbt, fee, setup } = res
					setFee(Number(fee))
					setPsbt(psbt)
					if (setup) {
						setSetup(setup)
					}
				} else if (key !== latest_key) {
					console.log(
						'[invoice] build result DISCARDED (stale) key=' + key + ' latestKey=' + latest_key
					)
				} else {
					// onBuild returned null without throwing — transient failure; show Try Again
					setError('Could not build transaction. Please try again.')
				}
			} catch (e) {
				console.log(
					'[invoice] build error: ' +
						((e as any)?.response?.data?.message || (e as any)?.message || e)
				)
				setError(friendlyTxError(e))
			} finally {
				// Only stop loading if this is still the latest request
				if (key === latest_key) {
					setLoadingPsbt(false)
				}
			}
		}

		if (onBuild && dFeeRate && dBuildDeps) {
			// Serialize the actual build inputs. dBuildDeps is already
			// memoized to stable identity when its JSON payload hasn't
			// changed, so this serialize is cheap and only produces a
			// novel string when the user actually changed something.
			const startKey = JSON.stringify([dFeeRate, retryCount, dBuildDeps], (_k, v) => {
				if (v instanceof Set) return Array.from(v).sort()
				if (v instanceof Map) return Array.from(v.keys()).sort()
				return v
			})
			if (startedKeyRef.current === startKey) {
				return
			}
			startedKeyRef.current = startKey
			run()
		}
	}, [isSignedIn, dBuildDeps, dFeeRate, retryCount, onBuild])

	const needPass = React.useMemo(
		// `Wallet.type()` and `isPasswordUnlocked()` read localStorage directly
		// — eslint can't see those reads, so it flagged `isSignedIn` as
		// "unnecessary" in PR #115. It isn't: when the user signs in, both of
		// those reads return new values, and `isSignedIn` is the only signal
		// in scope that flips at that moment. Without this dep, this memo
		// never re-evaluates after sign-in for ordinalswallet users → the
		// password unlock prompt is silently skipped → submit handlers like
		// inscribe-transfer's Split try to use a still-encrypted seed and
		// the action button click is a no-op. Keep `isSignedIn` here.
		() => {
			void isSignedIn
			return Wallet.type() === 'ordinalswallet' && !(isPasswordUnlocked() || validPass)
		},
		[isSignedIn, validPass]
	)

	const submitProblem = props.validateSubmit?.({ building: loadingPsbt, fee, psbt })

	const awaitingValidation = !!props.validateSubmit && (loadingPsbt || !psbt)
	const canRequestPassword = needPass && !submitProblem && !awaitingValidation

	const handleSubmit = React.useCallback(async () => {
		if (disabled || loadingPsbt || submitProblem || awaitingValidation) return
		if (needPass) {
			if (!pass.trim()) {
				setError('Enter a password')
				return
			}

			setLoadingPass(true)
			try {
				const { valid } = await getSeed(pass)
				if (!valid) {
					setError('Invalid Password')
					return
				}

				setValidPass(true)
				setError('')
			} finally {
				setLoadingPass(false)
			}
			return
		}

		// Only guard when Invoice owns the PSBT build, and only when psbt is
		// strictly null (never set). An empty string '' is a valid sentinel
		// used by callers like inscribe-transfer that return psbt:'' from
		// their estimate step and build the real PSBT inside their onSubmit.
		if (onBuild && psbt === null) {
			setError('Transaction not ready. Please wait and try again.')
			return
		}

		setLoading(true)

		try {
			await onSubmit(psbt ?? '', setup ?? undefined)
		} catch (e) {
			setError((e as any)?.response?.data?.message || (e as any)?.message)
		}

		setLoading(false)
	}, [
		psbt,
		setup,
		onSubmit,
		onBuild,
		pass,
		needPass,
		disabled,
		loadingPsbt,
		submitProblem,
		awaitingValidation
	])

	/** Whether the primary button (and Enter in the password field) is inert. */
	const submitBlocked = needPass
		? disabled || loading || loadingPass || !!submitProblem || awaitingValidation
		: loadingPsbt ||
		  disabled ||
		  !!submitProblem ||
		  loading ||
		  !!errorMessage ||
		  (!!onBuild && psbt === null)

	const handleConnectWallet = useCallback((evt: any) => {
		evt.stopPropagation()
		evt.preventDefault()
		event.emit('command', { id: 'connect-wallet' })
	}, [])

	return (
		<>
			{/* Reserve space only when not fixed. In fixed mode, overlap UI to save vertical space. */}
			{!fixed && !props.inline && <div style={{ height }} />}
			<div
				className={classNames(
					props.inline
						? 'relative w-full'
						: fixed
						? 'fixed bottom-0 left-0 right-0 w-full z-50'
						: 'absolute bottom-0 left-0 w-full',
					{ ['invisible']: !open }
				)}
			>
				<div
					className={classNames('border-t flex flex-col bg-popover w-full p-3 gap-1', {
						'pb-12': fixed
					})}
					ref={ref}
				>
					{items.map(renderItem)}
					{submitProblem && (
						<p
							role="status"
							className="my-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-relaxed text-foreground"
						>
							{submitProblem}
						</p>
					)}
					{showFee && (
						<div className="grid grid-cols-3 gap-2">
							{feeOptions.map((option) => (
								<div
									key={option.level}
									className="flex items-center cursor-pointer hover:bg-accent rounded-sm p-2"
									onClick={() => handleSelectFee(option)}
									role="button"
									tabIndex={0}
									onKeyDown={onKeyboardClick(() => handleSelectFee(option))}
								>
									<SelectButton
										onSelect={() => handleSelectFee(option)}
										selected={option.key === selected}
									/>
									<div className="flex flex-col ml-2">
										<span className="text-sm">{option.level}</span>
										<span className="text-xs text-muted-foreground">{option.rate} sats/vB</span>
									</div>
								</div>
							))}
						</div>
					)}
					{isSignedIn && (error || errorMessage) && (
						<div className="mt-2 space-y-0.5">
							<p className="text-xxs text-red-500">{error || errorMessage}</p>
							<p className="text-xxs text-muted-foreground">
								Need a hand? Email{' '}
								<a
									href={`mailto:support@ordinalswallet.com?subject=${encodeURIComponent(
										'Purchase problem: ' + String(error || errorMessage).slice(0, 80)
									)}`}
									className="underline hover:text-foreground"
								>
									support@ordinalswallet.com
								</a>{' '}
								and we&apos;ll sort it out.
							</p>
						</div>
					)}
					{isSignedIn ? (
						<>
							{canRequestPassword && (
								<Input
									onChange={handleChangePass}
									value={pass}
									placeholder="password"
									className="mt-2"
									type="password"
									// The panel is a cmdk palette: an Enter that bubbles selects the
									// highlighted row (it toggled another lot into the listing).
									// Enter here means "submit the password".
									onKeyDown={(evt) => {
										evt.stopPropagation()
										if (evt.key !== 'Enter') return
										evt.preventDefault()
										if (!submitBlocked) void handleSubmit()
									}}
								/>
							)}
							{!needPass && !loadingPsbt && error ? (
								<Button
									onClick={() => setRetryCount((c) => c + 1)}
									className="my-2 bg-violet-600 text-white font-bold hover:bg-violet-800 select-none"
								>
									Try Again
								</Button>
							) : (
								<Button
									onClick={handleSubmit}
									disabled={submitBlocked}
									className="my-2 bg-violet-600 text-white font-bold hover:bg-violet-800 select-none"
								>
									{canRequestPassword && (loadingPass ? <Loading /> : 'Enter Password')}
									{!loading &&
										(!needPass || !!submitProblem) &&
										!loadingPsbt &&
										(typeof action === 'function'
											? action({ building: false, fee, psbt })
											: action)}
									{(loading || loadingPsbt || awaitingValidation) &&
										(!needPass || awaitingValidation) && <Loading />}
								</Button>
							)}
						</>
					) : (
						<Button
							onClick={handleConnectWallet}
							className="my-2 bg-violet-600 text-white font-bold hover:bg-violet-800 select-none"
						>
							Connect Wallet
						</Button>
					)}
					<p className="text-xxs text-muted-foreground select-none">
						By clicking the button above you agree to the
						<Link target="_blank" href="/terms" className="ml-1 underline">
							Terms of Service
						</Link>
						.
					</p>
				</div>
			</div>
		</>
	)
}

export default Invoice
